import { waitForAgentReadiness } from './agentReadiness.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import { useVoiceRecorder } from './useVoiceRecorder.js';
import { createSilenceTracker } from './agentLifecycle.js';
import { createLivePreview, type PreviewState } from './liveTranscription.js';
import { attachLiveAudio } from './liveAudio.js';
export function useAgentCapture() {
    const [waking, setWaking] = useState(false);
    const [provider, setProvider] = useState<string | null>(null);
    const [slow, setSlow] = useState(false);
    const [error, setError] = useState(false);
    const [audio, setAudio] = useState<Blob | null>(null);
    const [hint, setHint] = useState(false);
    const generation = useRef(0);
    const abort = useRef<AbortController | null>(null);
    const recorder = useVoiceRecorder(120, (blob) => { setAudio(blob); setHint(false); });
    const [preview,setPreview]=useState<PreviewState>({text:'',status:'idle'});
    const live=useMemo(()=>createLivePreview({onUpdate:setPreview,transcribe:async(blob,signal)=>{
        const form=new FormData();form.append('audio',blob,'preview.wav');
        return api.upload<{text:string}>('/voice/transcribe-preview',form,signal);
    }}),[]);
    const hasMicrophone=recorder.state==='armed'||recorder.state==='recording'||recorder.state==='paused';
    const captureState=useRef(recorder.state);captureState.current=recorder.state;
    useEffect(()=>{
        if(!hasMicrophone)return;
        const stream=recorder.stream();if(!stream)return;
        const tap=attachLiveAudio(stream,(samples,rate)=>{if(captureState.current==='armed'||captureState.current==='recording')live.push(samples,rate);},()=>live.setAvailable(false));
        const timer=setInterval(()=>{void live.tick();},500);
        return ()=>{clearInterval(timer);tap.close();live.setActive(false);};
    },[hasMicrophone,recorder.stream,live]);
    useEffect(()=>{live.setActive(recorder.state==='recording');},[recorder.state,live]);
    useEffect(()=>()=>live.reset(),[live]);
    const tracker = useRef(createSilenceTracker());
    const manual = useRef(false);
    useEffect(() => {
        if (recorder.state !== 'armed' && recorder.state !== 'recording' && recorder.state !== 'paused')
            return;
        const timer = setInterval(() => {
            setHint(tracker.current.frame(recorder.level(), Date.now(), recorder.state === 'paused'));
            if (recorder.state === 'armed' && tracker.current.hasSpeech())
                recorder.activate();
        }, 100);
        return () => clearInterval(timer);
    }, [recorder.state, recorder.level]);
    useEffect(() => () => { generation.current += 1; abort.current?.abort(); }, []);
    const cancel = useCallback(() => {
        generation.current += 1;
        abort.current?.abort();
        recorder.discard();
        live.reset();
        setWaking(false);
        setHint(false);
        setAudio(null);
    }, [recorder.discard,live]);
    const start = async () => {
        const mine = ++generation.current;
        abort.current?.abort();
        const controller = new AbortController();
        abort.current = controller;
        setWaking(true);
        setError(false);
        setAudio(null);
        live.reset();
        setSlow(false);
        manual.current = false;
        tracker.current = createSilenceTracker();
        let firstRead = true;
        try {
            await waitForAgentReadiness({
                signal: controller.signal, now: Date.now, onSlow: () => setSlow(true),
                read: async () => { const path = firstRead ? '/voice/readiness?retry=1' : '/voice/readiness'; firstRead = false; const result = await api.get<{status:'ready'|'warming'|'unavailable';provider:string|null}>(path,controller.signal); if (mine === generation.current) setProvider(result.provider); return result.status; },
                wait: () => new Promise<void>((resolve) => {
                    const complete = () => { clearTimeout(timer); controller.signal.removeEventListener('abort', complete); resolve(); };
                    const timer = setTimeout(complete, 1500);
                    controller.signal.addEventListener('abort', complete, { once: true });
                }),
            });
            if (mine !== generation.current)
                return;
            await recorder.start(true);
            if(mine!==generation.current)return;
            setWaking(false);
        }
        catch {
            if (mine === generation.current) {
                setError(true);
                setWaking(false);
            }
        }
    };
    const stop = async () => { live.setActive(false); const blob = await recorder.stop(); if (blob && (tracker.current.hasSpeech() || manual.current))
        setAudio(blob); setHint(false); return blob ?? audio; };
    const manualStart = () => { manual.current = true; recorder.activate(); };
    return { ...recorder, preview, manualStart, provider, waking, slow, error: error || recorder.state === 'denied', audio, hint, start, stop, cancel };
}
